import { Toast, ToastContext } from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext } from 'react';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, createInsurance, updateInsurance } from '~shared/actions/insurances';
import { updateCashPay } from '~shared/actions/users';
import { getInferredInsuranceFormValues } from '~shared/features/insurances/helpers/getInferredInsuranceFormValues';
import { type Values } from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { removeEmptyInsuranceValues } from '~shared/features/insurances/helpers/removeEmptyInsuranceValues';
import { getSelectedInsuranceID } from '~shared/features/insurances/selectors/getInsurances';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getSelectedUser } from '~shared/features/users/selectors/getSelectedUser';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getInsuranceSource } from '~shared/lib/analytics/src/getInsuranceSource';
import { queries } from '~shared/queries/query-keys';
import { useGetInsuranceByID } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Insurance } from '~shared/types/clients';
import { InsuranceForm } from './InsuranceForm';

type Props = {
  readonly onCancel: () => void;
  readonly onShowPhotoForm: () => void;
};

export const InsuranceFormContainer = ({ onCancel, onShowPhotoForm }: Props) => {
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const selectedUser = useSelectorShared(getSelectedUser);
  const currentUser = useSelectorShared(getCurrentUser);
  const user = selectedUser || currentUser;
  const dispatch = useDispatchShared();
  const source = useSelectorShared(getInsuranceSource);
  const loading = useSelectorShared(
    (state) => state.ui.loading.createInsuranceLoading || state.ui.loading.updateInsuranceLoading,
  );
  const insuranceID = useSelectorShared(getSelectedInsuranceID);
  const { insurance: selectedInsurance } = useGetInsuranceByID(insuranceID);
  const insuranceError = useSelectorShared(
    (state) =>
      state.ui.errors.createInsuranceError ||
      state.ui.errors.updateInsuranceError ||
      state.ui.errors.createPhotoInsuranceError,
  );

  const handleShowPhotoForm = () => {
    dispatch(clearInsuranceError);
    onShowPhotoForm();
  };

  const onFinish = async (insurance: Insurance | false) => {
    if (!insurance) return;

    // update user's cash_pay_only when insurance is saved
    if (user?.cash_pay_only) {
      const { success } = await dispatch(updateCashPay(user.id, false));
      if (success) {
        trackEvent({
          event: EVENTS.CASH_PAY_ONLY_SET,
          params: {
            source,
            'new value': false,
          },
        });
      }
    }

    trackEvent({
      event: EVENTS.INSURANCE_ADDED,
      params: {
        type: 'manual',
        source,
        entity_name: insurance.insurance_plan_name ?? '',
      },
    });

    // either close add benefits form in onboarding or close insurance modal in user profile
    onCancel();
    queryClient.invalidateQueries({ queryKey: queries.insurances.fetchAll._def });
    queryClient.invalidateQueries({
      queryKey: queries.insurances.fetchOne({ dispatch, insuranceID: insurance.id }).queryKey,
    });
  };

  const onSubmit = async (values: Values) => {
    if (!user) return;
    const newInsuranceValues = getInferredInsuranceFormValues(values, user);

    if (selectedInsurance) {
      const insurance: Insurance | false = await dispatch(
        // eslint-disable-next-line import/no-deprecated
        updateInsurance({
          id: selectedInsurance.id,
          ...removeEmptyInsuranceValues(newInsuranceValues),
        }),
      );

      addToast(<Toast>Your insurance has been updated!</Toast>);
      onFinish(insurance);
    } else {
      const insurance = await dispatch(
        // eslint-disable-next-line import/no-deprecated
        createInsurance({
          user_id: user.id,
          ...removeEmptyInsuranceValues(newInsuranceValues),
        }),
      );
      addToast(<Toast>Your insurance has been saved!</Toast>);
      onFinish(insurance);
    }
  };

  return (
    <InsuranceForm
      insuranceError={insuranceError}
      loading={loading}
      onCancel={onCancel}
      onSubmit={onSubmit}
      selectedInsurance={selectedInsurance}
      onShowPhotoForm={handleShowPhotoForm}
    />
  );
};
